import React, { useState, useEffect } from 'react';
import { Table, Typography, notification, Space, Form, Input, Button } from 'antd';
import API_SERVICE from 'services/api-service';
import { PrimaryTooltip } from 'components';
import { CommonLinkProjects } from 'views/Claims/CommonLinkProjects';
import { formatCurrency } from 'services/util';

const { Text, Title } = Typography;

export default function ConsumablesTable({ claimId, claimDetails, type ,projectsList}: any) {
    const [consumableTableData, setConsumableTableData] = useState([] as any[]);
    const [consumablesCostTotal, setConsumableCostTotal] = useState({
        netAmount: 0,
        rnd: 0,
        total: 0,
    } as any);
    const [fetching, setFetching] = useState(false);
    const [showProjectsModal, setShowProjectsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);

    const [columns, setColumns] = useState([
        {
            title: 'S.No',
            dataIndex: 'sNO',
            width: '70px',
            render: (a: any, v: any, index: number) => <Text>{index + 1}</Text>,
        },
        {
            title: type === 'softwareConsumables' ? 'Software Consumable Name' : 'Consumable Name',
            dataIndex: 'name',
            width: '200px',
            render: (a: any) => <Text>{a}</Text>,
        },
        {
            title:  type === 'softwareConsumables' ? 'Software Consumable Type' : 'Consumable Type',
            dataIndex: type === 'softwareConsumables' ? 'softwareConsumableId' : 'consumableId',
            width: '200px',
            render: (a: any) => <Text>{a}</Text>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            width: '180px',
            render: (a: any) => <Text>{a}</Text>,
        },
        {
            title: 'Net Amount (GBP)',
            dataIndex: 'netAmount',
            width: '180px',
            render: (a: any) => <Text>{formatCurrency(a)}</Text>,
        },
        {
            title: 'R&D Allocation (%)',
            dataIndex: 'rndAllocationPercentage',
            width: '150px',
            render: (a: any) => <Text>{a} %</Text>,
        },
        {
            title: 'Total (GBP)',
            dataIndex: 'total',
            width: '150px',
            editable: false,
            render: (a: any) => <Text>{formatCurrency(a)}</Text>,
        },
          {
            title: (
                <PrimaryTooltip
                    title="Linked Projects"
                    text="Linked Projects"
                />
            ),
            dataIndex: '',
            width: '150px',
            render: (a: any, record: any, index: number) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                        setSelectedRecord(record);
                        setShowProjectsModal(true);
                    }}
                >
                    View Projects
                </Button>
            )
        },
    ]);

    const RDECColumn = [
        {
            title: (
                <PrimaryTooltip
                    title="prompt text"
                    text="RDEC Allocation (%)"
                />
            ),
            key: 'rdecAllocation',
            width: '200px',
            render: (a: any) => (
                <Text>{`${
                    100 - (a.smeAllocation ? a.smeAllocation : 0)
                } %`}</Text>
            ),
        },
        {
            title: (
                <PrimaryTooltip
                    title="prompt text"
                    text="RDEC Allocation (GBP)"
                />
            ),
            dataIndex: 'rdecTotal',
            width: '200px',
            render: (a: any) => (
                <Text>
                  {formatCurrency(a)}
                </Text>
            ),
        },
        
    ];

    const SMEColumn = [
        {
            title: (
                <PrimaryTooltip title="prompt text" text="SME Allocation (%)" />
            ),
            dataIndex: 'smeAllocation',
            width: '200px',
        },
        {
            title: (
                <PrimaryTooltip
                    title="prompt text"
                    text="SME Allocation (GBP)"
                />
            ),
            dataIndex: 'smeTotal',
            width: '200px',
            render: (a: any) => (
                <Text>
                    {formatCurrency(a)}
                </Text>
            ),
        },
    ];

    const MIXEDColumn = [
        {
            title: (
                <PrimaryTooltip title="prompt text" text="SME Allocation (%)" />
            ),
            dataIndex: 'smeAllocation',
            width: '200px',
        },
        {
            title: (
                <PrimaryTooltip
                    title="prompt text"
                    text="SME Allocation (GBP)"
                />
            ),
            dataIndex: 'smeTotal',
            width: '200px',
            render: (a: any) => (
                <Text>
                    {formatCurrency(a)}
                </Text>
            ),
        },
        {
            title: (
                <PrimaryTooltip
                    title="prompt text"
                    text="RDEC Allocation (%)"
                />
            ),
            key: 'rdecAllocation',
            width: '200px',
            render: (a: any) => (
                <Text>{`${
                    100 - (a.smeAllocation ? a.smeAllocation : 0)
                } %`}</Text>
            ),
        },
        {
            title: (
                <PrimaryTooltip
                    title="prompt text"
                    text="RDEC Allocation (GBP)"
                />
            ),
            dataIndex: 'rdecTotal',
            width: '200px',
            render: (a: any) => (
                <Text>
                   {formatCurrency(a)}
                </Text>
            ),
        },
    ];

    const getConsumablesCostList = async () => {
        setFetching(true);
        try {
            const {
                data: { data },
            } = type === 'softwareConsumables'
                ? await API_SERVICE.softwareConsumablesCostList(claimId)
                : await API_SERVICE.consumablesCostList(claimId);
            setConsumableTableData(data.items);
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        } finally {
            setFetching(false);
        }
    };

    const getConsumablesCostTotal = async () => {
        try {
            const result = type === 'softwareConsumables'
                ? await API_SERVICE.softwareConsumablesCostTotal(claimId)
                : await API_SERVICE.consumablesCostTotal(claimId);
            
            const {
                data: { data },
            } = result;
            setConsumableCostTotal(data);
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };

    const checkClaimType = () => {
        if (claimDetails?.type?.name === 'SME') {
            let newColumns: any = [...columns, ...SMEColumn];
            setColumns(newColumns);
        } else if (claimDetails?.type?.name === 'RDEC') {
            let newColumns: any = [...columns, ...RDECColumn];
            setColumns(newColumns);
        } else if (claimDetails?.type?.name === 'Mixed' || claimDetails?.type?.name === 'Merged') {
            console.log('mixed', MIXEDColumn);

            let newColumns: any = [...columns, ...MIXEDColumn];
            setColumns(newColumns);
        }
    };

    useEffect(() => {
        getConsumablesCostList();
        getConsumablesCostTotal();
        checkClaimType();
    }, []);

    return (
        <>
            <Title level={4}> {type === 'softwareConsumables' ? 'Software Consumables' : 'Consumables'}</Title>
            <Table
                loading={fetching}
                pagination={false}
                scroll={{ x: true }}
                columns={columns}
                dataSource={consumableTableData}
                bordered
            />

            <div className="d-flex align-items-center justify-content-end mt-3">
                <Space>
                    <Form.Item className="mb-0" style={{ width: '220px' }}>
                        <Input
                            disabled
                            value={`Direct Cost: ${consumablesCostTotal.directCost &&
                                consumablesCostTotal.directCost.toFixed(2)
                                } GBP`}
                        />
                    </Form.Item>
                    <Form.Item className="mb-0" style={{ width: '220px' }}>
                        <Input
                            disabled
                            value={`In-Direct Cost: ${consumablesCostTotal.inDirectCost &&
                                consumablesCostTotal.inDirectCost.toFixed(2)
                                } GBP`}
                        />
                    </Form.Item>
                    <Form.Item className="mb-0" style={{ width: '220px' }}>
                        <Input
                            disabled
                            value={`Net Amount: ${
                                consumablesCostTotal.netAmount &&
                                consumablesCostTotal.netAmount.toFixed(2)
                            } GBP`}
                        />
                    </Form.Item>
                    <Form.Item className="mb-0" style={{ width: '220px' }}>
                        <Input
                            disabled
                            value={`Total: ${
                                consumablesCostTotal.total &&
                                consumablesCostTotal.total.toFixed(2)
                            } GBP`}
                        />
                    </Form.Item>
                </Space>
            </div>
            {showProjectsModal && (
                <CommonLinkProjects
                    type='consumables'
                    displayOnly={true}
                    record={selectedRecord}
                    visible={showProjectsModal}
                    projectsList={projectsList}
                    onClose={() => setShowProjectsModal(false)}
                />
            )}
        </>
    );
}
